// # Payment History Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../auth/state';
import SharedHeader from '../../../../../components/SharedHeader';
import DashboardFooter from '../../../../../components/DashboardFooter';
import { collection, getDocs, getDoc, doc, deleteDoc, query, where, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import { FaSearch, FaEdit, FaTrash, FaExpand, FaChartLine, FaHistory, FaCalendarAlt, FaDollarSign, FaClock, FaSave, FaTimes, FaChartBar } from 'react-icons/fa';
import './pay_history.css';
import { nationalityData } from '../../../../../utils/nationality_data';

const PaymentHistory = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [activeTab, setActiveTab] = useState('general');
  const [payments, setPayments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalPayments: 0,
    totalAmount: 0,
    thisYearPayments: 0,
    thisYearAmount: 0,
    recentPayments: [],
    productStats: {}
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // # Add new state for filters and sorting
  const [filterOptions, setFilterOptions] = useState({
    product: 'all',
    dateRange: 'all',
    nationality: 'all',
    priceRange: 'all'
  });
  
  const [sortOption, setSortOption] = useState({
    field: 'timestamp',
    direction: 'desc'
  });

  // Move fetchPayments outside of useEffect
  const fetchPayments = async () => {
    setLoading(true);
    try {
      const paymentsRef = collection(db, 'financial');
      const paymentsSnap = await getDocs(paymentsRef);
      
      let allPayments = [];
      paymentsSnap.docs.forEach(doc => {
        // # Document ID now contains year_month format (e.g., "2024_03")
        const docId = doc.id;
        const yearMonthData = doc.data();
        
        // # Check if payments map exists in the document
        if (yearMonthData.payments) {
          // # Extract payments from the payments map
          Object.entries(yearMonthData.payments).forEach(([paymentId, paymentData]) => {
            // # Only process payment transactions
            if (paymentId.startsWith('payment_') && paymentData.transaction_type === 'payment') {
              // # Format the payment data using new structure
              const formattedPayment = {
                // # Use the payment ID as the payment_id
                payment_id: paymentId,
                // # Store the document ID (year_month) for later use
                doc_id: docId,
                
                // # User details
                name: paymentData.user?.name || '',
                email: paymentData.user?.email || '',
                phone: paymentData.user?.phone || '',
                nationality: paymentData.user?.nationality || '',
                userId: paymentData.user?.userId || '',
                
                // # Payment details
                amount: paymentData.pay?.amount_native_currency || 0,
                currency: paymentData.pay?.currency || '',
                currency_symbol: paymentData.pay?.currency_symbol || '',
                egp_total: paymentData.pay?.egp_total || 0,
                egp_net: paymentData.pay?.egp_net || 0,
                product: paymentData.pay?.product || '',
                duration: paymentData.pay?.duration || '',
                
                // # Core details
                type: paymentData.transaction_type || '',
                timestamp: paymentData.metadata?.created_at || '',
                
                // # Budget dividend details
                budget_dvd_amount: paymentData.budget_dvd?.egp_amount || 0,
                budget_dvd_percent: paymentData.budget_dvd?.percentage_of_total || 0,
                
                // # Mentor details
                mentor: paymentData.mentor?.mentor_name || '',
                mentor_email: paymentData.mentor?.email || '',
                mentor_id: paymentData.mentor?.uid || '',
                
                // # Tutor details (for tutor hosting)
                tutor: paymentData.tutor?.tutor_name || '',
                tutor_email: paymentData.tutor?.email || '',
                tutor_id: paymentData.tutor?.uid || '',
                
                // # Admin details
                admin: paymentData.register_admin || {},
                
                // # Keep shares for editing
                shares: paymentData.shares || {}
              };
              allPayments.push(formattedPayment);
            }
          });
        }
      });

      // # Sort payments by timestamp in descending order
      allPayments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setPayments(allPayments);

      // # Calculate stats with updated structure
      const currentYear = new Date().getFullYear();
      const thisYearPayments = allPayments.filter(
        payment => new Date(payment.timestamp).getFullYear() === currentYear
      );

      // # Calculate product stats
      const productStats = {};
      allPayments.forEach(payment => {
        const product = payment.product || 'unknown';
        if (!productStats[product]) {
          productStats[product] = {
            count: 0,
            amount: 0
          };
        }
        productStats[product].count += 1;
        productStats[product].amount += Number(payment.egp_total);
      });

      setStats({
        totalPayments: allPayments.length,
        totalAmount: allPayments.reduce((sum, p) => sum + Number(p.egp_total), 0),
        thisYearPayments: thisYearPayments.length,
        thisYearAmount: thisYearPayments.reduce((sum, p) => sum + Number(p.egp_total), 0),
        recentPayments: allPayments.slice(0, 5),
        productStats
      });

    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Then in useEffect, just call the function
  useEffect(() => {
    fetchPayments();
  }, []);

  // # Update the handleDeletePayment function to delete from both collections

  const handleDeletePayment = async (payment) => {
    if (!window.confirm(`Are you sure you want to delete payment from ${payment.name || 'Unknown'}?`)) {
      return;
    }
    
    setLoading(true);
    
    try {
      // # Get the document ID (year_month) and payment ID
      const docId = payment.doc_id; // Format: "2024_03"
      const paymentId = payment.payment_id;
      
      if (!docId || !paymentId) {
        throw new Error('Missing document ID or payment ID');
      }
      
      // # Reference to the financial document
      const financialRef = doc(db, 'financial', docId);
      
      // # Get the current document data
      const docSnap = await getDoc(financialRef);
      
      if (!docSnap.exists()) {
        throw new Error(`Document ${docId} not found`);
      }
      
      // # Get the document data
      const data = docSnap.data();
      
      // # Check if the payments map and the payment exists
      if (!data.payments || !data.payments[paymentId]) {
        throw new Error(`Payment ${paymentId} not found in document ${docId}`);
      }
      
      // # Create a new payments object without the payment
      const updatedPayments = { ...data.payments };
      delete updatedPayments[paymentId];
      
      // # Update the document with the modified payments map
      await updateDoc(financialRef, {
        payments: updatedPayments
      });
      
      // # Now also delete from adm_income collection if needed
      // # Extract year from docId (first 4 characters of "2024_03")
      const year = docId.split('_')[0];
      
      // # Reference to the adm_income document
      const admIncomeRef = doc(db, 'adm_income', year);
      
      // # Get the current document data
      const admIncomeSnap = await getDoc(admIncomeRef);
      
      if (admIncomeSnap.exists()) {
        const admIncomeData = admIncomeSnap.data();
        
        // # Create a new object with the payment removed from all team members and budget
        const updatedAdmIncomeData = { ...admIncomeData };
        
        // # Remove from budget allocations if it exists
        if (updatedAdmIncomeData.budget && updatedAdmIncomeData.budget[paymentId]) {
          delete updatedAdmIncomeData.budget[paymentId];
        }
        
        // # Remove from team member allocations
        Object.keys(updatedAdmIncomeData).forEach(key => {
          // # Skip non-team member keys
          if (key !== 'budget' && key !== 'metadata') {
            if (updatedAdmIncomeData[key] && updatedAdmIncomeData[key][paymentId]) {
              delete updatedAdmIncomeData[key][paymentId];
            }
          }
        });
        
        // # Update the adm_income document
        await setDoc(admIncomeRef, updatedAdmIncomeData);
      }
      
      // # Refresh the payments list
      fetchPayments();
      
      alert('Payment deleted successfully');
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert(`Error deleting payment: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // # Filter payments based on search query
  const filteredPayments = payments.filter(payment => 
    payment.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    payment.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    payment.payment_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // # Get this year's payments
  const thisYearPayments = payments.filter(
    payment => new Date(payment.timestamp).getFullYear() === new Date().getFullYear()
  );

  // # Redirect if not admin
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Handle payment selection
  const handlePaymentSelect = (payment) => {
    setSelectedPayment(payment);
    setEditFormData(payment);  // Initialize edit form data
    setIsEditing(false);
  };

  // # Handle edit mode toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    setEditFormData(selectedPayment);  // Reset form data to selected payment
  };

  // # Handle form field changes
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // # Handle form submission
  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      
      // # Use the stored doc_id (year_month) from the selected payment
      const docId = selectedPayment.doc_id;
      const paymentId = selectedPayment.payment_id;
      
      // # Get the financial document
      const financialRef = doc(db, 'financial', docId);
      const financialDoc = await getDoc(financialRef);
      
      if (!financialDoc.exists()) {
        console.error(`Document ${docId} does not exist`);
        alert(`Error: Document ${docId} does not exist`);
        setLoading(false);
        return;
      }
      
      // # Get the current data
      const data = financialDoc.data();
      
      // # Check if the payments map and the payment exists
      if (!data.payments || !data.payments[paymentId]) {
        console.error(`Payment ${paymentId} not found in document ${docId}`);
        alert(`Error: Payment ${paymentId} not found`);
        setLoading(false);
        return;
      }
      
      // # Get the original payment data
      const originalPayment = data.payments[paymentId];
      
      // # Structure the updated payment data according to the new format
      const updatedPayment = {
        // # Keep original metadata and add updated timestamp
        metadata: {
          ...originalPayment.metadata,
          updated_at: new Date().toISOString()
        },
        
        // # Keep original admin registration info
        register_admin: originalPayment.register_admin,
        
        // # Keep transaction type
        transaction_type: 'payment',
        
        // # Update budget dividend details
        budget_dvd: {
          egp_amount: parseFloat(editFormData.budget_dvd_amount),
          percentage_of_total: parseFloat(editFormData.budget_dvd_percent)
        },
        
        // # Update payment details
        pay: {
          product: editFormData.product,
          duration: editFormData.duration,
          amount_native_currency: parseFloat(editFormData.amount),
          currency: editFormData.currency,
          currency_symbol: editFormData.currency_symbol,
          egp_total: parseFloat(editFormData.egp_total),
          egp_net: parseFloat(editFormData.egp_total) - parseFloat(editFormData.budget_dvd_amount)
        },
        
        // # Update user details
        user: {
          name: editFormData.name,
          email: editFormData.email,
          phone: editFormData.phone,
          nationality: editFormData.nationality,
          userId: editFormData.userId
        },
        
        // # Keep original shares with net_share_egp and total_share_egp properties
        shares: originalPayment.shares,
        
        // # Keep mentor and tutor details
        mentor: originalPayment.mentor || {},
        tutor: originalPayment.tutor || {}
      };
      
      // # Update the payment in the payments map
      const updatedPayments = {
        ...data.payments,
        [paymentId]: updatedPayment
      };
      
      // # Update the document with the modified payments map
      await updateDoc(financialRef, {
        payments: updatedPayments
      });
      
      // # Refresh the payments list
      fetchPayments();
      
      // # Close the edit form
      setIsEditing(false);
      setSelectedPayment(null);
      
      alert('Payment updated successfully');
    } catch (error) {
      console.error('Error updating payment:', error);
      alert(`Error updating payment: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // # Update payment row to show product type instead of type
  const PaymentRow = ({ payment }) => (
    <div className="pay_hist_payment-row" onClick={() => handlePaymentSelect(payment)}>
      <button className="pay_hist_expand-btn" title="View Details">
        <FaExpand />
      </button>
      <div className="pay_hist_payment-info">
        <div className="pay_hist_payment-main">
          <h3>{payment.name}</h3>
          <p>{payment.email} • {payment.phone}</p>
        </div>
        <div className="pay_hist_payment-details">
          <span className="pay_hist_payment-type">{payment.product}</span>
          <span className="pay_hist_payment-amount">
            {payment.currency_symbol}{payment.amount} ({payment.currency}) | E£ {Number(payment.egp_total).toLocaleString()}
          </span>
          <span className="pay_hist_payment-date">{new Date(payment.timestamp).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );

  // # Update Modal Component with product field
  const PaymentFormModal = ({ payment, onClose }) => {
    if (!payment) return null;

    // # Helper function to group nationalities by region (reuse from above)
    const getNationalityGroups = () => {
      const groups = {
        'Arab Countries': [],
        'Asia': [],
        'Europe': [],
        'Americas': [],
        'Africa': [],
        'Oceania': []
      };

      // # First add Arab countries in specific order
      const arabCountriesOrder = [
        'EGYPT',  // Egypt first
        'SAUDI_ARABIA',
        'UAE',
        'KUWAIT',
        'QATAR',
        'BAHRAIN',
        'OMAN',
        'JORDAN',
        'LEBANON',
        'SYRIA',
        'IRAQ',
        'PALESTINE',
        'YEMEN',
        'LIBYA',
        'TUNISIA',
        'ALGERIA',
        'MOROCCO',
        'SUDAN',
        'SOMALIA',
        'MAURITANIA',
        'DJIBOUTI',
        'COMOROS'
      ];

      // # Add Arab countries in specified order
      arabCountriesOrder.forEach(countryCode => {
        if (nationalityData[countryCode]) {
          groups['Arab Countries'].push({
            code: countryCode,
            ...nationalityData[countryCode]
          });
        }
      });

      // # Add remaining countries by region
      Object.entries(nationalityData).forEach(([key, data]) => {
        if (arabCountriesOrder.includes(key)) return;

        let region = data.searchKeys.find(key => 
          key === 'asia' || 
          key === 'europe' || 
          key === 'americas' || 
          key === 'africa' || 
          key === 'oceania'
        );

        if (region) {
          region = region.charAt(0).toUpperCase() + region.slice(1);
          groups[region].push({
            code: key,
            ...data
          });
        }
      });

      // # Sort countries within each region alphabetically (except Arab Countries)
      Object.keys(groups).forEach(region => {
        if (region !== 'Arab Countries') {
          groups[region].sort((a, b) => a.name.localeCompare(b.name));
        }
      });

      return groups;
    };

    // # Get current nationality code from payment data
    const getCurrentNationalityCode = (paymentNationality) => {
      if (!paymentNationality) return 'EGYPT'; // Default to Egypt if no nationality

      const normalizedNationality = paymentNationality.toLowerCase();
      
      // Find matching country code
      const matchingCountry = Object.entries(nationalityData).find(([code, data]) => {
        return data.name.toLowerCase() === normalizedNationality ||
               data.searchKeys.some(key => normalizedNationality.includes(key.toLowerCase()));
      });

      return matchingCountry ? matchingCountry[0] : 'EGYPT';
    };

    // # Add a function to render the shares table with the new structure
    const renderSharesTable = () => {
      if (!payment.shares || Object.keys(payment.shares).length === 0) {
        return <p>No shares data available</p>;
      }

      return (
        <div className="pay_history_shares-table-container">
          <h3>Revenue Shares</h3>
          <table className="pay_history_shares-table">
            <thead>
              <tr>
                <th>Team Member</th>
                <th>Percentage</th>
                <th>Net Share (EGP)</th>
                <th>Total Share (EGP)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(payment.shares).map(([memberId, shareData]) => (
                <tr key={memberId}>
                  <td>{getMemberName(memberId)}</td>
                  <td>{shareData.percentage_of_net}%</td>
                  <td>E£ {formatCurrency(shareData.net_share_egp || 0)}</td>
                  <td>E£ {formatCurrency(shareData.total_share_egp || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    };

    return (
      <div className="pay_hist_modal-overlay" onClick={() => onClose()}>
        <div className="pay_hist_modal-content" onClick={e => e.stopPropagation()}>
          <div className="pay_hist_modal-header">
            <h2 className="pay_hist_modal-title">
              {isEditing ? 'Edit Payment' : 'Payment Details'}
            </h2>
            <div className="pay_hist_modal-actions">
              {isEditing ? (
                <>
                  <button className="pay_hist_modal-btn pay_hist_modal-save" onClick={handleSaveEdit}>
                    <FaSave /> Save Changes
                  </button>
                  <button className="pay_hist_modal-btn pay_hist_modal-cancel" onClick={handleEditToggle}>
                    <FaTimes /> Cancel
                  </button>
                </>
              ) : (
                <>
                  <button className="pay_hist_modal-btn pay_hist_modal-save" onClick={handleEditToggle}>
                    <FaEdit /> Edit
                  </button>
                  <button 
                    className="pay_hist_modal-btn pay_hist_modal-delete"
                    onClick={() => {
                      if (payment && payment.payment_id) {
                        handleDeletePayment(payment);
                      } else {
                        alert('Error: Cannot delete payment - Invalid payment ID');
                      }
                    }}
                  >
                    <FaTrash /> Delete
                  </button>
                  <button className="pay_hist_modal-btn pay_hist_modal-cancel" onClick={onClose}>
                    <FaTimes /> Close
                  </button>
                </>
              )}
            </div>
          </div>

          <form onSubmit={handleSaveEdit}>
            {/* Personal Information */}
            <div className="pay_hist_form-section">
              <h3>Personal Information</h3>
              <div className="pay_hist_form-row">
                <div className="pay_hist_form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    name="name"
                    value={isEditing ? editFormData.name : payment.name}
                    onChange={handleFieldChange}
                    disabled={!isEditing}
                  />
                </div>
                <div className="pay_hist_form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={isEditing ? editFormData.email : payment.email}
                    onChange={handleFieldChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>
              <div className="pay_hist_form-row">
                <div className="pay_hist_form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={isEditing ? editFormData.phone : payment.phone}
                    onChange={handleFieldChange}
                    disabled={!isEditing}
                  />
                </div>
                <div className="pay_hist_form-group">
                  <label>Nationality</label>
                  <select
                    name="nationality"
                    value={isEditing ? 
                      editFormData.nationality_code || getCurrentNationalityCode(editFormData.nationality) : 
                      getCurrentNationalityCode(payment.nationality)
                    }
                    onChange={(e) => {
                      const selectedCountry = nationalityData[e.target.value];
                      handleFieldChange({
                        target: {
                          name: 'nationality',
                          value: selectedCountry.name
                        }
                      });
                      handleFieldChange({
                        target: {
                          name: 'nationality_code',
                          value: e.target.value
                        }
                      });
                    }}
                    disabled={!isEditing}
                    className="pay_hist_nationality-select"
                  >
                    {Object.entries(getNationalityGroups()).map(([region, countries]) => (
                      countries.length > 0 && (
                        <optgroup key={region} label={`${region} (${countries.length})`}>
                          {countries.map(country => (
                            <option key={country.code} value={country.code}>
                              {country.flag} {country.name}
                            </option>
                          ))}
                        </optgroup>
                      )
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="pay_hist_form-section">
              <h3>Payment Information</h3>
              <div className="pay_hist_form-row">
                <div className="pay_hist_form-group">
                  <label>Amount ({payment.currency})</label>
                  <input
                    type="text"
                    name="amount"
                    value={isEditing ? editFormData.amount : payment.amount}
                    onChange={handleFieldChange}
                    disabled={!isEditing}
                  />
                </div>
                <div className="pay_hist_form-group">
                  <label>EGP Total</label>
                  <input
                    type="text"
                    name="egp_total"
                    value={isEditing ? editFormData.egp_total : payment.egp_total}
                    onChange={handleFieldChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>
              <div className="pay_hist_form-row">
                <div className="pay_hist_form-group">
                  <label>Product Type</label>
                  <select
                    name="product"
                    value={isEditing ? editFormData.product : payment.product}
                    onChange={handleFieldChange}
                    disabled={!isEditing}
                  >
                    <option value="step1_library">Step 1 Library Access</option>
                    <option value="step2_library">Step 2 Library Access</option>
                    <option value="step1_mentorship_monthly">Step 1 Mentorship Monthly</option>
                    <option value="step2_mentorship_monthly">Step 2 Mentorship Monthly</option>
                    <option value="step1_mentorship_premium">Step 1 Mentorship Premium</option>
                    <option value="step2_mentorship_premium">Step 2 Mentorship Premium</option>
                    <option value="OET">OET</option>
                    <option value="step1_tutor_hosting">Step 1 Tutor Hosting</option>
                    <option value="step2_tutor_hosting">Step 2 Tutor Hosting</option>
                    <option value="US_VISA_Counselling">US VISA Counselling</option>
                  </select>
                </div>
                <div className="pay_hist_form-group">
                  <label>Duration</label>
                  <input
                    type="text"
                    name="duration"
                    value={isEditing ? editFormData.duration : payment.duration}
                    onChange={handleFieldChange}
                    disabled={!isEditing || payment.product?.includes('mentorship')}
                    placeholder={payment.product?.includes('mentorship') ? '1 month (auto)' : 'Enter duration'}
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="pay_hist_form-section">
              <h3>Additional Information</h3>
              <div className="pay_hist_form-row">
                <div className="pay_hist_form-group">
                  <label>Mentor</label>
                  <input
                    type="text"
                    name="mentor"
                    value={isEditing ? editFormData.mentor : payment.mentor}
                    onChange={handleFieldChange}
                    disabled={!isEditing}
                  />
                </div>
                <div className="pay_hist_form-group">
                  <label>User ID</label>
                  <input
                    type="text"
                    value={payment.userId}
                    disabled={true}
                  />
                </div>
              </div>
              <div className="pay_hist_form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={isEditing ? editFormData.notes : payment.notes}
                  onChange={handleFieldChange}
                  disabled={!isEditing}
                  rows={4}
                />
              </div>
            </div>

            {/* Admin Information */}
            <div className="pay_hist_form-section">
              <h3>Admin Information</h3>
              <div className="pay_hist_form-row">
                <div className="pay_hist_form-group">
                  <label>Admin Email</label>
                  <input
                    type="text"
                    value={payment.admin?.email || ''}
                    disabled={true}
                  />
                </div>
                <div className="pay_hist_form-group">
                  <label>Payment ID</label>
                  <input
                    type="text"
                    value={payment.payment_id}
                    disabled={true}
                  />
                </div>
              </div>
            </div>

            {/* Add the shares table to the appropriate section */}
            <div className="pay_history_payment-section">
              <h3>Revenue Distribution</h3>
              {renderSharesTable()}
            </div>
          </form>
        </div>
      </div>
    );
  };

  // # Helper function to format product names
  const formatProductName = (productName) => {
    switch(productName) {
      case 'step1_library':
        return 'Step 1 Library';
      case 'step2_library':
        return 'Step 2 Library';
      case 'step1_mentorship_monthly':
        return 'Step 1 Mentorship (Monthly)';
      case 'step2_mentorship_monthly':
        return 'Step 2 Mentorship (Monthly)';
      case 'step1_mentorship_premium':
        return 'Step 1 Mentorship (Premium)';
      case 'step2_mentorship_premium':
        return 'Step 2 Mentorship (Premium)';
      case 'OET':
        return 'OET';
      case 'step1_tutor_hosting':
        return 'Step 1 Tutor Hosting';
      case 'step2_tutor_hosting':
        return 'Step 2 Tutor Hosting';
      case 'US_VISA_Counselling':
        return 'US VISA Counselling';
      default:
        return productName;
    }
  };

  // # Helper function to get product color
  const getProductColor = (product) => {
    switch (true) {
      case product.includes('library'):
        return '#8884d8'; // Purple
      case product.includes('mentorship'):
        return '#82ca9d'; // Green
      case product.includes('premium'):
        return '#ffc658'; // Yellow
      default:
        return '#ff8042'; // Orange (default)
    }
  };

  // # Helper function to get member name from ID
  const getMemberName = (memberId) => {
    const memberNames = {
      'ahmed_hadidy': 'Ahmed Elhadidy',
      'ahmed_ayman': 'Ahmed Ayman',
      'ahmed_hussein': 'Ahmed Hussein',
      'hossam_gomaa': 'Hossam Gomaa',
      'nabil_mostafa': 'Nabil Mostafa'
    };
    
    return memberNames[memberId] || memberId;
  };

  // # Helper function to format currency values
  const formatCurrency = (value) => {
    return Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // # Helper function to group nationalities by region with Arab countries first
  const getNationalityGroups = () => {
    const groups = {
      'Arab Countries': [],
      'Asia': [],
      'Europe': [],
      'Americas': [],
      'Africa': [],
      'Oceania': []
    };

    // # First add Arab countries in specific order
    const arabCountriesOrder = [
      'EGYPT',  // Egypt first
      'SAUDI_ARABIA',
      'UAE',
      'KUWAIT',
      'QATAR',
      'BAHRAIN',
      'OMAN',
      'JORDAN',
      'LEBANON',
      'SYRIA',
      'IRAQ',
      'PALESTINE',
      'YEMEN',
      'LIBYA',
      'TUNISIA',
      'ALGERIA',
      'MOROCCO',
      'SUDAN',
      'SOMALIA',
      'MAURITANIA',
      'DJIBOUTI',
      'COMOROS'
    ];

    // # Add Arab countries in specified order
    arabCountriesOrder.forEach(countryCode => {
      if (nationalityData[countryCode]) {
        groups['Arab Countries'].push({
          code: countryCode,
          ...nationalityData[countryCode]
        });
      }
    });

    // # Add remaining countries by region
    Object.entries(nationalityData).forEach(([key, data]) => {
      // Skip if already added to Arab Countries
      if (arabCountriesOrder.includes(key)) {
        return;
      }

      // Determine region from searchKeys
      let region = data.searchKeys.find(key => 
        key === 'asia' || 
        key === 'europe' || 
        key === 'americas' || 
        key === 'africa' || 
        key === 'oceania'
      );

      if (region) {
        region = region.charAt(0).toUpperCase() + region.slice(1);
        groups[region].push({
          code: key,
          ...data
        });
      }
    });

    // # Sort countries within each region alphabetically (except Arab Countries)
    Object.keys(groups).forEach(region => {
      if (region !== 'Arab Countries') {
        groups[region].sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    return groups;
  };

  // # Filter payments based on all criteria
  const getFilteredPayments = (paymentsToFilter) => {
    return paymentsToFilter.filter(payment => {
      // # Product filter
      if (filterOptions.product !== 'all' && payment.product !== filterOptions.product) {
        return false;
      }

      // # Date range filter
      if (filterOptions.dateRange !== 'all') {
        const paymentDate = new Date(payment.timestamp);
        const today = new Date();
        switch (filterOptions.dateRange) {
          case 'last7':
            if ((today - paymentDate) > 7 * 24 * 60 * 60 * 1000) return false;
            break;
          case 'last30':
            if ((today - paymentDate) > 30 * 24 * 60 * 60 * 1000) return false;
            break;
          case 'last90':
            if ((today - paymentDate) > 90 * 24 * 60 * 60 * 1000) return false;
            break;
        }
      }

      // # Updated nationality filter
      if (filterOptions.nationality !== 'all') {
        const selectedCountry = nationalityData[filterOptions.nationality];
        if (!selectedCountry) return false;

        const paymentNationalityLower = payment.nationality?.toLowerCase() || '';
        
        // Check for exact name match
        const matchesName = selectedCountry.name.toLowerCase() === paymentNationalityLower;
        
        // Check for search key matches
        const matchesSearchKeys = selectedCountry.searchKeys.some(key => 
          paymentNationalityLower.includes(key.toLowerCase())
        );
        
        // Check for Arabic name match if available
        const matchesArabic = selectedCountry.searchKeys.some(key => 
          /[\u0600-\u06FF]/.test(key) && // Check if key contains Arabic characters
          paymentNationalityLower.includes(key)
        );

        if (!matchesName && !matchesSearchKeys && !matchesArabic) return false;
      }

      // # Price range filter
      const amount = Number(payment.egp_total);
      switch (filterOptions.priceRange) {
        case 'under1000':
          if (amount >= 1000) return false;
          break;
        case '1000to5000':
          if (amount < 1000 || amount > 5000) return false;
          break;
        case 'over5000':
          if (amount <= 5000) return false;
          break;
      }

      return true;
    });
  };

  // # Sort payments based on selected option
  const getSortedPayments = (paymentsToSort) => {
    return [...paymentsToSort].sort((a, b) => {
      switch (sortOption.field) {
        case 'amount':
          return sortOption.direction === 'asc' 
            ? Number(a.egp_total) - Number(b.egp_total)
            : Number(b.egp_total) - Number(a.egp_total);
        case 'name':
          return sortOption.direction === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        case 'timestamp':
        default:
          return sortOption.direction === 'asc'
            ? new Date(a.timestamp) - new Date(b.timestamp)
            : new Date(b.timestamp) - new Date(a.timestamp);
      }
    });
  };

  // # Add FilterAndSort component
  const FilterAndSort = () => (
    <div className="pay_hist_filter-sort-container">
      <div className="pay_hist_filters">
        <select
          value={filterOptions.product}
          onChange={(e) => setFilterOptions({...filterOptions, product: e.target.value})}
          className="pay_hist_filter-select"
        >
          <option value="all">All Products</option>
          <option value="step1_library">Step 1 Library</option>
          <option value="step2_library">Step 2 Library</option>
          <option value="step1_mentorship_monthly">Step 1 Mentorship Monthly</option>
          <option value="step2_mentorship_monthly">Step 2 Mentorship Monthly</option>
          <option value="step1_mentorship_premium">Step 1 Mentorship Premium</option>
          <option value="step2_mentorship_premium">Step 2 Mentorship Premium</option>
          <option value="OET">OET</option>
          <option value="step1_tutor_hosting">Step 1 Tutor Hosting</option>
          <option value="step2_tutor_hosting">Step 2 Tutor Hosting</option>
          <option value="US_VISA_Counselling">US VISA Counselling</option>
        </select>

        <select
          value={filterOptions.dateRange}
          onChange={(e) => setFilterOptions({...filterOptions, dateRange: e.target.value})}
          className="pay_hist_filter-select"
        >
          <option value="all">All Time</option>
          <option value="last7">Last 7 Days</option>
          <option value="last30">Last 30 Days</option>
          <option value="last90">Last 90 Days</option>
        </select>

        <select
          value={filterOptions.nationality}
          onChange={(e) => setFilterOptions({...filterOptions, nationality: e.target.value})}
          className="pay_hist_filter-select"
        >
          <option value="all">All Nationalities</option>
          {Object.entries(getNationalityGroups()).map(([region, countries]) => (
            countries.length > 0 && (
              <optgroup key={region} label={`${region} (${countries.length})`}>
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </optgroup>
            )
          ))}
        </select>

        <select
          value={filterOptions.priceRange}
          onChange={(e) => setFilterOptions({...filterOptions, priceRange: e.target.value})}
          className="pay_hist_filter-select"
        >
          <option value="all">All Prices</option>
          <option value="under1000">Under E£1,000</option>
          <option value="1000to5000">E£1,000 - E£5,000</option>
          <option value="over5000">Over E£5,000</option>
        </select>
      </div>

      <div className="pay_hist_sort">
        <select
          value={`${sortOption.field}-${sortOption.direction}`}
          onChange={(e) => {
            const [field, direction] = e.target.value.split('-');
            setSortOption({ field, direction });
          }}
          className="pay_hist_sort-select"
        >
          <option value="timestamp-desc">Date (Newest First)</option>
          <option value="timestamp-asc">Date (Oldest First)</option>
          <option value="amount-desc">Amount (Highest First)</option>
          <option value="amount-asc">Amount (Lowest First)</option>
          <option value="name-asc">Name (A-Z)</option>
          <option value="name-desc">Name (Z-A)</option>
        </select>
      </div>
    </div>
  );

  // # Update the All History and This Year tabs to use filters and sorting
  const renderPaymentsList = (paymentsToRender) => {
    const filteredPayments = getFilteredPayments(paymentsToRender);
    const sortedPayments = getSortedPayments(filteredPayments);

    return (
      <>
        <FilterAndSort />
        <div className="pay_hist_payments-list">
          {sortedPayments.map(payment => (
            <PaymentRow key={payment.payment_id} payment={payment} />
          ))}
          {sortedPayments.length === 0 && (
            <div className="pay_hist_no-results">
              No payments found matching the selected filters
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="pay_hist_page">
      <SharedHeader />
      
      <main className="pay_hist_content">
        <div className="pay_hist_header">
          <h1>Payment History</h1>
          <button 
            className="pay_hist_back-button"
            onClick={() => navigate('/admin/financial/payments')}
          >
            Back to Payments
          </button>
        </div>

        {/* Tabs */}
        <div className="pay_hist_tabs">
          <button 
            className={`pay_hist_tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <FaChartLine /> General Stats
          </button>
          <button 
            className={`pay_hist_tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <FaHistory /> All History
          </button>
          <button 
            className={`pay_hist_tab ${activeTab === 'thisYear' ? 'active' : ''}`}
            onClick={() => setActiveTab('thisYear')}
          >
            <FaCalendarAlt /> This Year
          </button>
        </div>

        {/* Tab Content */}
        <div className="pay_hist_tab-content">
          {/* General Stats Dashboard */}
          {activeTab === 'general' && (
            <div className="pay_hist_stats-dashboard">
              <div className="pay_hist_stats-grid">
                <div className="pay_hist_stat-card">
                  <FaDollarSign className="pay_hist_stat-icon" />
                  <h3>Total Payments</h3>
                  <p className="pay_hist_stat-value">{stats.totalPayments}</p>
                  <p className="pay_hist_stat-amount">E£ {stats.totalAmount.toLocaleString()}</p>
                </div>
                <div className="pay_hist_stat-card">
                  <FaCalendarAlt className="pay_hist_stat-icon" />
                  <h3>This Year</h3>
                  <p className="pay_hist_stat-value">{stats.thisYearPayments}</p>
                  <p className="pay_hist_stat-amount">E£ {stats.thisYearAmount.toLocaleString()}</p>
                </div>
                <div className="pay_hist_stat-card">
                  <FaClock className="pay_hist_stat-icon" />
                  <h3>Recent Activity</h3>
                  <div className="pay_hist_recent-list">
                    {stats.recentPayments.map(payment => (
                      <div key={payment.payment_id} className="pay_hist_recent-item">
                        <span>{payment.name}</span>
                        <span>E£ {Number(payment.egp_total).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Product Stats Section */}
              <div className="pay_hist_product-stats-section">
                <h3 className="pay_hist_section-title">
                  <FaChartBar className="pay_hist_section-icon" /> 
                  Payments by Product
                </h3>
                <div className="pay_hist_product-grid">
                  {Object.entries(stats.productStats)
                    .sort((a, b) => b[1].amount - a[1].amount) // Sort by amount (highest first)
                    .slice(0, 6) // Limit to top 6 products
                    .map(([product, data]) => (
                      <div key={product} className="pay_hist_product-card">
                        <h4>{formatProductName(product)}</h4>
                        <div className="pay_hist_product-stats-row">
                          <div className="pay_hist_product-count">
                            <span className="pay_hist_product-label">Payments</span>
                            <span className="pay_hist_product-value">{data.count}</span>
                          </div>
                          <div className="pay_hist_product-amount">
                            <span className="pay_hist_product-label">Revenue</span>
                            <span className="pay_hist_product-value">E£ {data.amount.toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="pay_hist_product-bar">
                          <div 
                            className="pay_hist_product-progress" 
                            style={{ 
                              width: `${(data.amount / stats.totalAmount) * 100}%`,
                              backgroundColor: getProductColor(product)
                            }}
                          ></div>
                        </div>
                        <div className="pay_hist_product-percentage">
                          {((data.amount / stats.totalAmount) * 100).toFixed(1)}% of total revenue
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          )}

          {/* All History */}
          {activeTab === 'all' && (
            <div className="pay_hist_all-history">
              <div className="pay_hist_search-bar">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search by name, email, or payment ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {renderPaymentsList(filteredPayments)}
            </div>
          )}

          {/* This Year */}
          {activeTab === 'thisYear' && (
            <div className="pay_hist_this-year">
              {renderPaymentsList(thisYearPayments)}
            </div>
          )}
        </div>
      </main>

      {/* Add Modal */}
      {selectedPayment && (
        <PaymentFormModal 
          payment={selectedPayment} 
          onClose={() => setSelectedPayment(null)} 
        />
      )}

      <DashboardFooter />
    </div>
  );
};

export default PaymentHistory;
